import { useEffect, useRef, useState } from 'react';

/**
 * Tooltip — wraps any element and shows a dark tooltip on hover.
 *
 * Props:
 *   text        — tooltip content (string or JSX)
 *   position    — 'top' | 'bottom' | 'left' | 'right'  (default: 'top')
 *   maxWidth    — max width of tooltip box in px         (default: 240)
 *   delay       — ms before tooltip appears              (default: 120)
 *   children    — the element to wrap
 *
 * Usage:
 *   <Tooltip text="Role credits are consumed when you publish a job.">
 *     <button>Publish</button>
 *   </Tooltip>
 */
export default function Tooltip({ text, children, position = 'top', maxWidth = 240, delay = 120 }) {
  const [visible, setVisible]   = useState(false);
  const [coords,  setCoords]    = useState({ top: 0, left: 0 });
  const wrapRef  = useRef(null);
  const timerRef = useRef(null);

  const show = () => {
    timerRef.current = setTimeout(() => {
      if (!wrapRef.current) return;
      const rect = wrapRef.current.getBoundingClientRect();
      const GAP  = 8;
      let top, left;

      if (position === 'top') {
        top  = rect.top  + window.scrollY - GAP;
        left = rect.left + window.scrollX + rect.width / 2;
      } else if (position === 'bottom') {
        top  = rect.bottom + window.scrollY + GAP;
        left = rect.left   + window.scrollX + rect.width / 2;
      } else if (position === 'left') {
        top  = rect.top  + window.scrollY + rect.height / 2;
        left = rect.left + window.scrollX - GAP;
      } else {
        top  = rect.top   + window.scrollY + rect.height / 2;
        left = rect.right + window.scrollX + GAP;
      }
      setCoords({ top, left });
      setVisible(true);
    }, delay);
  };

  const hide = () => {
    clearTimeout(timerRef.current);
    setVisible(false);
  };

  useEffect(() => () => clearTimeout(timerRef.current), []);

  if (!text) return children;

  const ARROW = 5;
  const arrowStyle = (pos) => {
    const base = { position:'absolute', width:0, height:0, border:`${ARROW}px solid transparent`, pointerEvents:'none' };
    if (pos === 'top')    return { ...base, top:'100%',  left:'50%', transform:'translateX(-50%)', borderTopColor:'#1E293B' };
    if (pos === 'bottom') return { ...base, bottom:'100%', left:'50%', transform:'translateX(-50%)', borderBottomColor:'#1E293B' };
    if (pos === 'left')   return { ...base, left:'100%',  top:'50%',  transform:'translateY(-50%)', borderLeftColor:'#1E293B' };
    return                       { ...base, right:'100%', top:'50%',  transform:'translateY(-50%)', borderRightColor:'#1E293B' };
  };

  const boxStyle = () => {
    const base = {
      position:'fixed', zIndex:99999, pointerEvents:'none',
      background:'#1E293B', color:'#F8FAFC', fontSize:12, fontWeight:400,
      padding:'7px 11px', borderRadius:7, lineHeight:1.55,
      maxWidth, width:'max-content', boxShadow:'0 4px 14px rgba(0,0,0,0.25)',
      fontFamily:"'Inter',-apple-system,sans-serif", textAlign:'center',
      wordBreak:'break-word',
    };
    if (position === 'top')    return { ...base, top:coords.top,  left:coords.left, transform:'translate(-50%, -100%)' };
    if (position === 'bottom') return { ...base, top:coords.top,  left:coords.left, transform:'translateX(-50%)' };
    if (position === 'left')   return { ...base, top:coords.top,  left:coords.left, transform:'translate(-100%, -50%)' };
    return                            { ...base, top:coords.top,  left:coords.left, transform:'translateY(-50%)' };
  };

  return (
    <>
      <span ref={wrapRef} style={{ display:'inline-flex', alignItems:'center' }}
        onMouseEnter={show} onMouseLeave={hide} onFocus={show} onBlur={hide}>
        {children}
      </span>
      {visible && (
        <span style={boxStyle()}>
          {text}
          <span style={arrowStyle(position)} />
        </span>
      )}
    </>
  );
}

/**
 * TooltipIcon — renders a small ⓘ icon with a tooltip on hover.
 * Drop anywhere next to a label to add contextual help.
 *
 * Usage:
 *   <TooltipIcon text="Role credits are consumed when you publish a job." />
 */
export function TooltipIcon({ text, position = 'top', size = 14, color = '#94A3B8' }) {
  return (
    <Tooltip text={text} position={position}>
      <svg width={size} height={size} viewBox="0 0 20 20" fill="none"
        style={{ display:'inline-block', verticalAlign:'middle', cursor:'help', flexShrink:0 }}>
        <circle cx="10" cy="10" r="9" stroke={color} strokeWidth="1.5" />
        <text x="10" y="14.5" textAnchor="middle" fontSize="11" fontWeight="600"
          fill={color} fontFamily="'Inter',-apple-system,sans-serif">i</text>
      </svg>
    </Tooltip>
  );
}
